// src/App.jsx
import Background from "./components/Background";
import SeoMetaTags from "./components/SeoMetaTags";
import { Music2 } from "lucide-react";
import { useLocation, Outlet, Link, NavLink } from "react-router-dom";
import { useGtagPageview, trackMarketingCta } from "./utils/ga";

const PUBLIC_NAV = [
  { to: "/pricing", label: "Pricing" },
  { to: "/knowledge-base", label: "Guides" },
  { to: "/about", label: "About" },
];

function Brand({ interactive, subtitle = "Song Requests • Event Photo Wall" }) {
  const baseClasses =
    "flex items-center gap-3 " + (interactive ? "group cursor-pointer select-none" : "select-none cursor-default");

  const content = (
    <>
      <div className="grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-zinc-950 ring-1 ring-emerald-400/30 shadow-[0_0_24px_-8px_rgba(16,185,129,0.6)] group-hover:opacity-95 transition">
        <Music2 size={20} />
      </div>
      <div className="text-left">
        <div className="text-lg font-semibold tracking-tight leading-none">
          <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-300 bg-clip-text text-transparent drop-shadow-[0_1px_0_rgba(0,0,0,0.4)]">
            DJ Request
          </span>
        </div>
        <div className="text-[11px] -mt-0.5 opacity-60">{subtitle}</div>
      </div>
    </>
  );

  if (interactive) {
    return (
      <Link to="/" className={baseClasses} aria-label="Go to the DJ Request home page">
        {content}
      </Link>
    );
  }

  return <div className={baseClasses}>{content}</div>;
}

export default function App() {
  const location = useLocation();

  useGtagPageview();

  const isLanding = location.pathname === "/";
  const isPhotoWall = location.pathname.startsWith("/wall");
  const isAppRoute = /^\/(dj|join|wall|album|qr-poster|return)(\/|$)/.test(location.pathname);
  const noindexPathMatchers = [
    /^\/dj$/,
    /^\/join\/[^/]+$/,
    /^\/wall\/[^/]+(?:\/.*)?$/,
    /^\/album\/[^/]+$/,
    /^\/qr-poster$/,
    /^\/signin$/,
    /^\/return$/,
    /^\/feedback$/,
    /^\/support$/,
  ];
  const shouldNoindex = noindexPathMatchers.some((matcher) => matcher.test(location.pathname));

  return (
    <div className="relative flex min-h-dvh flex-col">
      {shouldNoindex && (
        <SeoMetaTags
          robots="noindex,nofollow,noarchive"
          canonicalPath={location.pathname}
        />
      )}

      {!isPhotoWall && <Background />}

      {!isPhotoWall && (
        <header className="sticky top-0 z-20 border-b border-white/10 bg-zinc-950/85 md:backdrop-blur [padding-top:env(safe-area-inset-top)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-2 sm:px-4 py-3">
            {isLanding ? <Brand interactive={false} /> : <Brand interactive />}
            {!isAppRoute && (
              <nav aria-label="Primary" className="flex items-center gap-1 sm:gap-2 text-sm">
                {PUBLIC_NAV.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `inline-flex min-h-10 items-center rounded-lg px-2.5 py-1.5 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 ${
                        isActive ? "text-white" : "text-zinc-300"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                <Link
                  to="/signin"
                  onClick={() => trackMarketingCta({ ctaId: "header_start_event", destination: "/signin" })}
                  className="ml-1 hidden min-h-10 items-center rounded-lg bg-emerald-400 px-3 py-1.5 font-semibold text-zinc-950 transition hover:bg-emerald-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200 sm:inline-flex"
                >
                  Start an event
                </Link>
              </nav>
            )}
          </div>
        </header>
      )}

      <main
        className={`mx-auto w-full min-w-0 flex-1 ${isPhotoWall ? "" : "max-w-6xl px-2 py-6 sm:px-4 sm:py-8"}`}
      >
        <Outlet />
      </main>

      {!isPhotoWall && (
        <footer
          className="mt-auto border-t border-white/10 bg-zinc-950/85 md:backdrop-blur [padding-bottom:env(safe-area-inset-bottom)]"
        >
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 px-2 sm:px-4 py-3 text-xs text-zinc-300">
            <span className="min-w-0 truncate pr-2 opacity-70" suppressHydrationWarning>
              © {new Date().getFullYear()} Naranja LLC • DJ Request • All Vibes Welcomed.
            </span>

            <div className="flex shrink-0 items-center gap-2">
              <nav className="hidden sm:flex items-center gap-3" aria-label="Footer">
                <Link to="/about" className="hover:text-white underline underline-offset-2 decoration-emerald-400">
                  About
                </Link>
                <Link to="/pricing" className="hover:text-white underline underline-offset-2 decoration-emerald-400">
                  Pricing
                </Link>
                <Link to="/knowledge-base" className="hover:text-white underline underline-offset-2 decoration-emerald-400">
                  Knowledge Base
                </Link>
                <Link to="/privacy" className="hover:text-white underline underline-offset-2 decoration-emerald-400">
                  Privacy
                </Link>
                <Link to="/terms" className="hover:text-white underline underline-offset-2 decoration-emerald-400">
                  Terms
                </Link>
              </nav>

              <Link
                to="/support"
                className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-zinc-900/60 px-3 py-1.5 text-zinc-100 hover:bg-zinc-900/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                aria-label="Open the Support page"
              >
                Contact Support
              </Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
